<script setup lang="ts">
import {computed, ref} from 'vue';
import useCpBem from '~/composables/useCpBem';
import CpText from '~/components/cp/CpText/CpText.vue';
import CpButtonIcon from '~/components/cp/CpButtonIcon/CpButtonIcon.vue';

import type {ICpTextVariants, ICpTypeWithModifier} from '~/composables/useCpType';

const props = withDefaults(defineProps<{
  title: string,
  titleType?: ICpTypeWithModifier,
  titleVariant: ICpTextVariants,
  expanded: boolean,
  card?: boolean
  hideBottomBorder?: boolean
  hideTopBorder?: boolean
}>(), {
  titleType: 'neutral-dark',
  titleVariant: 'heading-h3',
  card: true,
});

const panel = ref(props.expanded ? 'accordion' : undefined);

const {bm, b, e} = useCpBem('cp-card-accordion');

const titleClasses = computed(() => {
  return [
      ...bm({addBorder: panel.value === 'accordion'}),
      ...bm({borderCardTitle: props.card}),
      ...bm({hideTitleBottomBorder: panel.value === undefined && props.hideBottomBorder}),
      ...bm({hideTopBorder: !!props.hideTopBorder}),
      e('v-expansion-panel-title'),
  ];
});

const contentClasses = computed(() => {
  return [
      ...bm({removeBorder: panel.value === 'accordion'}),
      ...bm({borderBottom: panel.value === 'accordion' && !props.card && !props.hideBottomBorder}),
      ...bm({borderCardText: props.card}),
      e('v-expansion-panel-text')];
});

const emit = defineEmits<{
  (e: 'update:expand', val: boolean): void
}>();

const iconAccordion = computed(() => {
  return panel.value === 'accordion' ? 'expand_less' : 'expand_more';
});

const onClickPanel = () => {
  if (panel.value === 'accordion') {
    emit('update:expand', true);
  } else {
    emit('update:expand', false);
  }
};

</script>

<template>
    <div :class="b">
        <v-expansion-panels v-model="panel" @click="onClickPanel">
            <v-expansion-panel :eager="true" elevation="0" value="accordion">
                <v-expansion-panel-title :eager="true" :class="titleClasses">
                    <CpText :variant="titleVariant" :type="titleType">
                        {{ title }}
                    </CpText>
                    <template #actions>
                        <CpButtonIcon :class="e`icon`" :kind="iconAccordion" data-testid="card-accordion-button" />
                    </template>
                </v-expansion-panel-title>
                <v-expansion-panel-text :eager="true" :class="contentClasses">
                    <div :class="e`slot-container`">
                        <slot />
                    </div>
                </v-expansion-panel-text>
            </v-expansion-panel>
        </v-expansion-panels>
    </div>
</template>

<style scoped lang="scss">
  @import "assets/style/global.scss";
  .cp-card-accordion {
    .v-expansion-panel--active > .v-expansion-panel-title {
      min-height: 56px;
    }

    &__icon {
      margin-left: 4px;
    }

    &__v-expansion-panel-title {
      padding: $cp-gutter;
      font-size: inherit;
      min-height: 56px;
      line-height: inherit;
      border-radius: 0;
      border-top: $cp-border-standard;
      border-bottom: $cp-border-standard;
      border-right: 0;
      border-left: 0;
      color: var(--color-white);

      --v-activated-opacity: 0;
    }

    &__slot-container {
      padding: $cp-gutter;
      border-radius: 0;
      border-left: 0;
      border-right: 0;
    }

    &__v-expansion-panel-text {
      border-bottom: 0;
      :deep(.v-expansion-panel-text__wrapper) {
        padding: 0;
      }
    }

    &--border-card-title {
      border-radius: $cp-border-radius;
      border: $cp-border-standard;
    }

    &--border-card-text {
      border-radius: $cp-border-radius;
      border-bottom: $cp-border-standard;
      border-left: $cp-border-standard;
      border-right: $cp-border-standard;
    }

    &--add-border {
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    }

    &--hide-title-bottom-border {
      border-bottom: 0;
    }

    &--remove-border {
      border-top: 0;
      border-top-left-radius: 0;
      border-top-right-radius: 0;
    }

    &--border-bottom {
      border-bottom: $cp-border-standard;
    }

    &--hide-top-border {
      border-top: 0;
    }

  }
</style>

<style lang="scss">
  .v-expansion-panel {
    background-color: transparent !important;
  }
</style>
